/**
 * editorTitleBarLayout — where the file title + save status live, and why.
 *
 * They used to be centred at the top of the canvas, i.e. exactly on top of
 * Excalidraw's centred toolbar: the title and the "Saved · just now" chip
 * covered the tool icons.
 *
 * They now sit in the top-LEFT column, immediately right of the ☰ menu button
 * (the conventional "document name" spot — Figma, Google Docs), and are width-
 * capped so they stop short of the toolbar at every viewport width.
 *
 * Geometry (measured in the running app; Excalidraw's own left properties
 * island uses the same envelope, x 16 → 218 at 1024px):
 *
 *   ☰ button:  x 16 → 52          (1rem + 36px)
 *   title bar: x 60 → 50vw - 308  (starts 8px after ☰)
 *   toolbar:   x 50vw - 295 → 50vw + 295   (fixed-width centred island)
 *
 * so the title bar always stops TOOLBAR_GAP_PX short of the toolbar's left
 * edge. Verified by editorTitleBarLayout.test.ts at 1024/1280/1440.
 */

/** Left edge of the title column: just past the ☰ menu button. */
export const TITLE_BAR_LEFT_PX = 60;
export const TITLE_BAR_LEFT = `${TITLE_BAR_LEFT_PX / 16}rem`;

/** Half-width of Excalidraw's centred toolbar island (measured). */
export const TOOLBAR_HALF_WIDTH_PX = 295;

/** Breathing room kept between the title column and the toolbar. */
export const TOOLBAR_GAP_PX = 13;

/** Never grow the title beyond this, however wide the screen. */
export const TITLE_BAR_CAP_PX = 352;

// 60 + (50vw - 368) = 50vw - 308 = toolbarLeft - 13. Clamped so it can neither
// go negative on narrow screens (it just ellipsises away) nor sprawl on wide
// ones.
export const TITLE_BAR_MAX_WIDTH = `clamp(0px, calc(50vw - ${
  (TITLE_BAR_LEFT_PX + TOOLBAR_HALF_WIDTH_PX + TOOLBAR_GAP_PX) / 16
}rem), ${TITLE_BAR_CAP_PX / 16}rem)`;

/** Left edge of the centred toolbar at a given viewport width. */
export const toolbarLeftEdge = (viewportWidth: number): number =>
  viewportWidth / 2 - TOOLBAR_HALF_WIDTH_PX;

/** Right edge of the title column at a given viewport width (mirrors the CSS). */
export const titleBarRightEdge = (viewportWidth: number): number => {
  const available =
    viewportWidth / 2 -
    (TITLE_BAR_LEFT_PX + TOOLBAR_HALF_WIDTH_PX + TOOLBAR_GAP_PX);
  const width = Math.min(Math.max(available, 0), TITLE_BAR_CAP_PX);
  return TITLE_BAR_LEFT_PX + width;
};
